import type { Feature, LineString } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types";
import type { BehaviorSubject } from "rxjs";
import { Status } from "@iventis/domain-model/model/status";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { createStaticStyleValue, getStaticStyleValue } from "@iventis/layer-style-helpers";
import { LineStyle } from "@iventis/domain-model/model/lineStyle";
import { getDefaultStyleProperty } from "../../utilities/style-helpers";
import { LayerStorageScope, MapState } from "../../types/store-schema";
import type { DomainLayer } from "../../state/map.state.types";
import { Layer } from "./layer";
import { ViewByStatus } from "../../types/layer-view-by";
import { defaultLineStyle } from "../../utilities/default-style-values";
import { getLineModelGuideLineLayerId } from "../mapbox/engine-mapbox-helpers";

export abstract class LineModelGuideLineLayer extends Layer<Feature<LineString, MapObjectProperties>[], LineStyle> {
    public readonly layerId: string;

    protected readonly sourceId: string;

    protected readonly guideLineLayer: DomainLayer;

    constructor(state$: BehaviorSubject<MapState>, lineModelLayerId: string) {
        super(state$);
        this.layerId = getLineModelGuideLineLayerId(lineModelLayerId);
        this.sourceId = this.layerId;
        this.guideLineLayer = this.createGuideLineLayer();
    }

    private createGuideLineLayer(): DomainLayer {
        const layer = this.getIventisLayer();
        const alwaysShowGuideLines = getStaticStyleValue(layer.lineModelStyle?.lineModelShowLine) ?? false;
        let lineStyle = defaultLineStyle;
        if (alwaysShowGuideLines) {
            lineStyle = {
                ...defaultLineStyle,
                colour: layer.lineModelStyle?.lineModelLineColour ?? getDefaultStyleProperty(StyleType.LineModel, "lineModelLineColour"),
                width: layer.lineModelStyle?.lineModelLineWidth ?? getDefaultStyleProperty(StyleType.LineModel, "lineModelLineWidth"),
                type: layer.lineModelStyle?.lineModelLineType ?? getDefaultStyleProperty(StyleType.LineModel, "lineModelLineType"),
                outline: layer.lineModelStyle?.lineModelLineOutline ?? getDefaultStyleProperty(StyleType.LineModel, "lineModelLineOutline"),
                outlineColour: layer.lineModelStyle?.lineModelLineOutlineColour ?? getDefaultStyleProperty(StyleType.LineModel, "lineModelLineOutlineColour"),
                outlineWidth: layer.lineModelStyle?.lineModelLineOutlineWidth ?? getDefaultStyleProperty(StyleType.LineModel, "lineModelLineOutlineWidth"),
            };
        } else {
            lineStyle = {
                ...defaultLineStyle,
                colour: createStaticStyleValue("black"),
            };
        }
        return {
            id: this.layerId,
            status: Status.Active,
            mapId: "",
            visible: layer.visible,
            locked: false,
            imported: false,
            name: "line model guide",
            styleType: StyleType.Line,
            areaStyle: undefined,
            lineStyle,
            pointStyle: undefined,
            iconStyle: undefined,
            modelStyle: undefined,
            lineModelStyle: undefined,
            areaModelStyle: undefined,
            previewIconUrl: "",
            dataFields: [],
            tooltipDataFieldIds: [],
            createdByUserId: "",
            createdByUserName: "",
            createdAt: undefined,
            lastUpdatedByUserId: "",
            lastUpdatedByUserName: "",
            lastUpdatedAt: undefined,
            viewBy: ViewByStatus.OBJECT,
            source: this.sourceId,
            stamp: "",
            selected: false,
            storageScope: LayerStorageScope.LocalOnly,
            remote: false,
        };
    }
}
